import { t } from "@/i18n-js/instance";
import { isStreamEnded } from "@/react/components/Events/EditForm/helpers";
import { useEventRsvp } from "@/react/components/Events/Rsvp/hooks/useEventRsvp";
import { useOnCreateLiveStream } from "@/react/components/Events/Rsvp/useOnCreateLiveStream";
import { LOCATION_TYPES } from "@circle-react/components/Events/helpers/constants";
import { dateIsPast } from "@circle-react/helpers/dateTimeHelpers";
import { liveStreamPaths } from "@circle-react/helpers/urlHelpers";
import type { Event } from "@circle-react/types/Event";
import { Button } from "@circle-react-uikit/Button";
import { useLive } from "./useLive";

interface JoinButtonProps {
  event: Event;
  full?: boolean;
  onChange: () => void;
}

export const JoinButton = ({ event, onChange, full }: JoinButtonProps) => {
  const { rsvpWhileJoiningEvent } = useEventRsvp({ event, onChange });

  const { event_setting_attributes: eventData, rsvped_event: isRsvpedEvent } =
    event || {};
  const {
    location_type: locationType,
    live_stream_slug: liveStreamSlug,
    virtual_location_url: virtualLocationUrl,
    ends_at: endsAt,
  } = eventData || {};
  const isEventEnded = dateIsPast(endsAt);
  const onCreateLiveStreamMutation = useOnCreateLiveStream(event);
  const { isLiveCreationError, canCreateLive } = useLive(event);

  const autoRsvp = () => {
    if (!isRsvpedEvent) {
      void rsvpWhileJoiningEvent();
    }
  };

  let label: any, url: any;
  switch (locationType) {
    case LOCATION_TYPES.LIVE_STREAM:
    case LOCATION_TYPES.LIVE_ROOM:
      label = t("events.locations.join_live");
      url =
        liveStreamSlug && liveStreamPaths.room({ roomSlug: liveStreamSlug });
      break;
    case LOCATION_TYPES.VIRTUAL:
    default:
      url = virtualLocationUrl;
      label = t("events.locations.join_event");
      break;
  }
  const shouldDisableButton = isEventEnded || !url;

  if (
    locationType === LOCATION_TYPES.IN_PERSON ||
    locationType === LOCATION_TYPES.TBD
  ) {
    return null;
  }

  const shouldCreateLiveRoom =
    canCreateLive && (isLiveCreationError || isStreamEnded(event));

  if (shouldCreateLiveRoom) {
    const isLiveStream = locationType === LOCATION_TYPES.LIVE_STREAM;
    return (
      <Button
        variant="primary"
        full={full}
        onClick={() => onCreateLiveStreamMutation.mutate()}
        disabled={onCreateLiveStreamMutation.isLoading}
      >
        {isLiveStream
          ? t(`events.locations.create_live_stream`)
          : t("events.locations.create_live_room")}
      </Button>
    );
  }

  const handleOnClick = () => {
    autoRsvp();
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <Button
      variant="primary"
      full={full}
      onClick={handleOnClick}
      disabled={shouldDisableButton}
      className="!font-semibold"
    >
      {label}
    </Button>
  );
};
